import { Colors } from "@App/whiteLabelReducer";

const whiteColorHexcode = "#ffffff";
const blackColorHexcode = "#000000";

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  hex = hex?.replace(/^#/, "");

  // Convert to RGB
  const r = parseInt(hex?.slice(0, 2), 16) / 255;
  const g = parseInt(hex?.slice(2, 4), 16) / 255;
  const b = parseInt(hex?.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        throw new Error("Unexpected max value in HSL conversion");
    }

    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return { h, s, l };
};

export const HSLToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const hexR = Math.round((r + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const hexG = Math.round((g + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const hexB = Math.round((b + m) * 255)
    .toString(16)
    .padStart(2, "0");

  return `#${hexR}${hexG}${hexB}`;
};

export const adjustLightness = (hex: string, percentage: number): string => {
  if (hex?.toLowerCase() === whiteColorHexcode || hex?.toLowerCase() === "#fff") {
    return "#e3e3e3";
  }

  // eslint-disable-next-line prefer-const
  let { h, s, l } = hexToHSL(hex);

  l = Math.min(Math.max(l + percentage, 0), 100);
  let newHex = HSLToHex(h, s, l);
  if (newHex === whiteColorHexcode) {
    newHex = adjustLightness(hex, percentage - 10);
  }
  return newHex;
};

export const hexToRgb = (hex: any) => {
  hex = hex?.replace(/^#/, "");

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return { r, g, b };
};

export const rgbToHex = (r: any, g: any, b: any) =>
  `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex?.length === 1 ? `0${hex}` : hex;
    })
    .join("")}`;

export const generateShades = (baseColor: string): string[] => {
  const baseHSL = hexToHSL(baseColor);
  const originalBase = hexToHSL("#ed3a56");
  const originalShades = [
    hexToHSL("#fee9ef"),
    hexToHSL("#fdc8d3"),
    hexToHSL("#ec919c"),
    hexToHSL("#e26575"),
  ];

  const adjustments = originalShades.map((shade) => ({
    s: shade.s - originalBase.s,
    l: shade.l - originalBase.l,
  }));

  return adjustments.map(({ s: sAdjust, l: lAdjust }) => {
    const newS = Math.max(0, Math.min(100, baseHSL.s + sAdjust));
    const newL = Math.max(0, Math.min(100, baseHSL.l + lAdjust));
    return HSLToHex(baseHSL.h, newS, newL);
  });
};

export const calculateLuminance = ({ r, g, b }: any) => {
  // Convert to 0-1 range
  r /= 255;
  g /= 255;
  b /= 255;

  // Apply gamma correction
  // eslint-disable-next-line no-restricted-properties
  const gammaCorrect = (c: any) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  r = gammaCorrect(r);
  g = gammaCorrect(g);
  b = gammaCorrect(b);

  // Calculate relative luminance using WCAG formula
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const getFontColorForBackground = (hex: string) =>
  isLightTheme(hex) ? blackColorHexcode : whiteColorHexcode; // black : white

export const isLightTheme = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  const luminance = calculateLuminance(rgb);
  return luminance > 0.179; // Threshold can be adjusted (0.179 is WCAG standard)
};
// return luminance > 0.5; // If luminance is greater than 0.5, it's a light theme

export const getContrastRatio = (hexColor1: string, hexColor2: string) => {
  const lum1 = calculateLuminance(hexToRgb(hexColor1));
  const lum2 = calculateLuminance(hexToRgb(hexColor2));

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

export const darkenRgb = ({ r, g, b }: any, factor: any) => ({
  r: Math.round(r * (1 - factor)),
  g: Math.round(g * (1 - factor)),
  b: Math.round(b * (1 - factor)),
});

export const getAccessibleColor = (
  brandColor: string,
  ctaColor: string,
  backgroundColor = whiteColorHexcode,
  targetRatio = 4.5,
) => {
  const currentRatio = getContrastRatio(brandColor, backgroundColor);

  if (currentRatio >= targetRatio) {
    return brandColor;
  }
  return ctaColor;
};

const giveActiveBackgroundColor = (color: string) => {
  if (color.includes(whiteColorHexcode)) {
    return "#F6F8FA";
  }
  return color;
};

const getLightnessFactorForHoverColor = (color: string): number => {
  const currentRatio = getContrastRatio(color, blackColorHexcode);
  if (currentRatio >= 1 && currentRatio <= 1.5) {
    return 90;
  }
  if (currentRatio > 1.5 && currentRatio <= 2) {
    return 70;
  }
  if (currentRatio > 2 && currentRatio <= 3) {
    return 60;
  }

  return 50;
};

export const processColors = (data: any) => {
  const colors: Colors = {
    brand_color_0: "",
    brand_color_1: "",
    brand_color_2: "",
    brand_color_3: "",
    brand_color: "",
    cta_color: "",
    footer_bg_color: "",
    primary_hover_color: "",
    primary_text_color: "",
    primary_text_hover_color: "",
    cta_text_color: "",
    cta_hover_color: "",
    button_hover_color: "",
    footer_text_color: "",
    footer_text_hover_color: "",
    accessible_brand_color: "",
    active_background_color: "",
  };
  const brandColor = data?.config?.brand_color;
  const ctaColor = data?.config?.listing?.cta_color;
  const footerColor = data?.config?.footer?.bg_color;
  const accessibleBrandColor = getAccessibleColor(brandColor, ctaColor);
  colors.brand_color = brandColor;
  colors.accessible_brand_color = accessibleBrandColor;
  [colors.brand_color_0, colors.brand_color_1, colors.brand_color_2, colors.brand_color_3] =
    generateShades(brandColor);
  colors.cta_color = ctaColor;
  colors.footer_bg_color = footerColor;
  colors.primary_hover_color = adjustLightness(
    accessibleBrandColor,
    isLightTheme(accessibleBrandColor) ? 10 : 40,
  );
  colors.cta_hover_color = adjustLightness(
    ctaColor,
    isLightTheme(ctaColor) ? 10 : getLightnessFactorForHoverColor(ctaColor),
  );
  colors.button_hover_color = adjustLightness(ctaColor, isLightTheme(ctaColor) ? 10 : 20);
  //  white or black color based on the background color, for dark themes it will be white and for light themes it will be black
  colors.primary_text_color = getFontColorForBackground(brandColor);
  colors.cta_text_color = getFontColorForBackground(ctaColor);
  colors.footer_text_color = getFontColorForBackground(footerColor);
  colors.primary_text_hover_color = adjustLightness(colors.primary_text_color, 20);
  colors.footer_text_hover_color = adjustLightness(colors.footer_text_color, 20);
  colors.active_background_color = isLightTheme(brandColor)
    ? giveActiveBackgroundColor(colors.brand_color_1)
    : adjustLightness(brandColor, 80);
  const themes = {
    isBrandThemeLight: isLightTheme(brandColor),
    isCtaThemeLight: isLightTheme(ctaColor),
    isFooterThemeLight: isLightTheme(footerColor),
  };
  return { colors, themes };
};
